import { z } from "zod";
import { defineTool } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult } from "./helpers.js";

const SP_DESCRIPTION = "Story point value (1=Common, 2=Magic, 3=Rare, 5=Unique)";

export function buildDiscoveryTools(connection: AgentConnection) {
  return [
    defineTool(
      "update_task_properties",
      "Set one or more task properties in a single call. All fields are optional — only include the fields you want to update.",
      {
        title: z.string().optional().describe("The new task title"),
        storyPointValue: z.number().optional().describe(SP_DESCRIPTION),
        tagNames: z.array(z.string()).optional().describe("Array of tag names to assign"),
        githubPRUrl: z
          .string()
          .url()
          .optional()
          .describe("GitHub pull request URL to link to this task"),
        githubBranch: z
          .string()
          .optional()
          .describe("Set the GitHub branch name for this task (e.g. 'conveyor/my-feature-abc123')"),
      },
      async ({ title, storyPointValue, tagNames, githubPRUrl, githubBranch }) => {
        try {
          await connection.call("updateTaskProperties", {
            sessionId: connection.sessionId,
            title,
            storyPointValue,
            tagNames,
            githubPRUrl,
            githubBranch,
          });

          const updatedFields = [];
          if (title !== undefined) updatedFields.push(`title to "${title}"`);
          if (storyPointValue !== undefined)
            updatedFields.push(`story points to ${storyPointValue}`);
          if (tagNames !== undefined) updatedFields.push(`tags (${tagNames.length} tag(s))`);
          if (githubPRUrl !== undefined) updatedFields.push(`PR link to "${githubPRUrl}"`);
          if (githubBranch !== undefined) updatedFields.push(`branch to "${githubBranch}"`);

          return textResult(`Task properties updated: ${updatedFields.join(", ")}`);
        } catch (error) {
          return textResult(
            `Failed to update task properties: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      },
    ),
  ];
}
